'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { LanguageSelectionCard } from '@/components/ui/LanguageSelectionCard';
import { getDefaultLanguage, STORAGE_KEYS } from '@/lib/i18n-utils';
import { Language } from '@/types/i18n';

export default function RootPage() {
  const router = useRouter();
  const [showLanguageSelection, setShowLanguageSelection] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);

  const handleLanguageSelect = (language: Language) => {
    setSelectedLanguage(language);
    setShowLanguageSelection(false);
    
    try {
      // Save language preference to localStorage
      localStorage.setItem(STORAGE_KEYS.LANGUAGE_PREFERENCE, language);
      localStorage.setItem(STORAGE_KEYS.FIRST_VISIT, 'true');
    } catch (error) {
      console.warn('Error saving language preference:', error);
    }
    
    // Redirect to language-specific route after a short delay for smooth transition
    setTimeout(() => {
      router.replace(`/${language}`);
    }, 300);
  };

  // Check if user has already selected a language before
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE_PREFERENCE);
      const hasVisited = localStorage.getItem(STORAGE_KEYS.FIRST_VISIT);
      
      if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'fr')) {
        // User has a saved preference, redirect immediately
        setSelectedLanguage(savedLanguage as Language);
        setShowLanguageSelection(false);
        router.replace(`/${savedLanguage}`);
      } else if (hasVisited) {
        // Returning visitor without language preference, use default
        const defaultLanguage = getDefaultLanguage();
        setSelectedLanguage(defaultLanguage);
        setShowLanguageSelection(false);
        router.replace(`/${defaultLanguage}`);
      }
      // If neither condition is met, show language selection (first visit)
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
      // In case of error, show language selection
    }
  }, [router]);

  return (
    <div className="min-h-screen bg-background">
      <LanguageSelectionCard
        isVisible={showLanguageSelection}
        onLanguageSelect={handleLanguageSelect}
      />
    </div>
  );
}
